import React from 'react';
import { useTheme } from '../../ThemeContext';

interface ConnectionPreviewProps {
  data: { columns: string[]; rows: any[][] } | null;
  loading: boolean;
  error: string | null;
  onPreview: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  label?: string;
}

export const ConnectionPreview: React.FC<ConnectionPreviewProps> = ({
  data,
  loading,
  error,
  onPreview,
  disabled = false,
  disabledMessage = '',
  label = 'Preview'
}) => {
  const { theme } = useTheme();

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={onPreview}
        disabled={disabled}
        title={disabled ? disabledMessage : label}
        style={{
          padding: '6px 10px',
          border: `1px solid ${theme.border}`,
          borderRadius: 6,
          background: disabled ? theme.hoverBackground : theme.cardBackground,
          color: disabled ? theme.textSecondary : theme.text,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        {label}
      </button>
      {disabled && disabledMessage && (
        <div style={{ marginTop: 6, fontSize: 12, color: theme.textSecondary }}>
          {disabledMessage}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        {loading && <div style={{ color: theme.textSecondary }}>Loading preview…</div>}
        {error && (
          <div
            style={{
              background: theme.errorLight,
              color: theme.errorDark,
              border: `1px solid ${theme.error}`,
              padding: 8,
              borderRadius: 6
            }}
          >
            {error}
          </div>
        )}
        {data && data.columns && data.columns.length > 0 && (
          <div
            style={{
              overflowX: 'auto',
              background: theme.tableHeaderBg,
              border: `1px solid ${theme.tableBorder}`,
              borderRadius: 8
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {data.columns.map((c) => (
                    <th
                      key={c}
                      style={{
                        textAlign: 'left',
                        padding: 8,
                        fontSize: 12,
                        color: theme.text,
                        borderBottom: `1px solid ${theme.tableBorder}`
                      }}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((v, j) => (
                      <td
                        key={j}
                        style={{
                          padding: 8,
                          fontSize: 12,
                          color: theme.text,
                          borderBottom: `1px solid ${theme.tableBorder}`
                        }}
                      >
                        {v === null || v === undefined ? '—' : String(v)}
                      </td>
                    ))}
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={data.columns.length}
                      style={{ padding: 8, color: theme.textSecondary }}
                    >
                      No rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
